<template>
  <div class="listar-materia-prima  mt-4">
    <!-- Título -->
    <h3 class="mb-2 text-materia">
      <font-awesome-icon icon="box" class="me-2" />
      Materias Primas
    </h3>

    <!-- Indicador de vista actual -->
    <div class="mb-4">
      <span class="badge fs-6 px-3 py-2 rounded-pill"
        :class="sinStock ? 'bg-danger-subtle text-danger fw-bold' : 'bg-success-subtle text-success fw-bold'">
        {{ sinStock ? 'SIN Stock' : 'Con Stock Disponible' }}
      </span>
    </div>

    <!-- Filtros -->
    <div class="toolbar bg-materia-light border rounded p-3 d-flex flex-wrap gap-3 mb-4">
      <div class="flex-grow-1">
        <label class="small fw-semibold text-secondary">Código Materia Prima</label>
        <input v-model="filtros.codigo" type="text" class="form-control" placeholder="Ej: MAT-001" />
      </div>
      <div class="flex-grow-1">
        <label class="small fw-semibold text-secondary">Código de Origen</label>
        <input v-model="filtros.codigo_origen" type="text" class="form-control" placeholder="Ej: BIOM-001" />
      </div>
      <div class="flex-grow-1">
        <label class="small fw-semibold text-secondary">Origen</label>
        <select v-model="filtros.origen" class="form-select">
          <option value="">Todos</option>
          <option value="biomasa">Biomasa</option>
          <option value="materia_prima">Materia Prima</option>
        </select>
      </div>
      <div class="d-flex align-items-end">
        <button @click="limpiarFiltros" class="btn btn-outline-materia">Limpiar Filtros</button>
      </div>
    </div>

    <!-- Total -->
    <p class="text-muted mb-4"><strong>{{ filteredMaterias.length }}</strong> ítem(s) encontrados</p>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-materia" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Cards -->
    <div v-else-if="filteredMaterias.length" class="row g-3">
      <div v-for="item in paginatedMaterias" :key="item.id" class="col-12 col-sm-6 col-md-4 col-lg-3">
        <MateriaPrimaCard :materia="item" @editar="editMateria" @transformar="transformarProductoFinal"
          @copiar="copiar" @procesar="procesarMateriaPrima" />
      </div>
    </div>


    <p v-else class="text-muted">No hay materias primas registradas.</p>

    <!-- Paginación -->
    <nav v-if="totalPages > 1" class="mt-4">
      <ul class="pagination justify-content-center small">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="currentPage--">Anterior</button>
        </li>
        <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: currentPage === page }">
          <button class="page-link" @click="currentPage = page">{{ page }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="currentPage++">Siguiente</button>
        </li>
      </ul>
    </nav>

    <!-- Toast -->
    <div v-if="copiado" class="copiado-toast">Copiado al portapapeles</div>
  </div>
</template>




<script>
import axios from '@/axios';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MateriaPrimaCard from './MateriaPrimaCard.vue';


export default {
  name: 'ListarMateriaPrima',
  components: { FontAwesomeIcon, MateriaPrimaCard },
  data() {
    return {
      materias: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      clubActual: this.$store.getters.getClubActual,
      filtros: {
        origen: '',
        codigo: '',
        codigo_origen: ''
      },
      orden: {
        campo: 'id',
        asc: true
      },
      copiado: false,
      detallesVisibles: []
    };
  },
  props: {
    sinStock: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    sinStock() {
      this.fetchMaterias();
    }
  },
  computed: {
    filteredMaterias() {
      return this.materias.map(item => ({
        ...item,
        showDetails: item.showDetails || false
      })).filter(item => {
        let match = true;

        if (this.filtros.origen) {
          if (this.filtros.origen === 'biomasa' && !item.biomasa) match = false;
          if (this.filtros.origen === 'materia_prima' && (!Array.isArray(item.procesos_resultado) || item.procesos_resultado.length === 0)) match = false;
        }

        if (this.filtros.codigo && !item.codigo.toLowerCase().includes(this.filtros.codigo.toLowerCase())) {
          match = false;
        }

        if (this.filtros.codigo_origen) {
          const codigoOrigen = item.biomasa
            ? item.biomasa.tipo_biomasa
            : (Array.isArray(item.procesos_resultado) && item.procesos_resultado.length > 0)
              ? item.procesos_resultado[0].proceso_transformacion.insumos[0].materia_prima.codigo
              : '';
          if (!codigoOrigen.toLowerCase().includes(this.filtros.codigo_origen.toLowerCase())) {
            match = false;
          }
        }

        return match;
      }).sort((a, b) => {
        const campo = this.orden.campo;
        let valA = a[campo];
        let valB = b[campo];

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return this.orden.asc ? -1 : 1;
        if (valA > valB) return this.orden.asc ? 1 : -1;
        return 0;
      });
    },
    paginatedMaterias() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredMaterias.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.filteredMaterias.length / this.pageSize);
    }
  },
  methods: {
    async fetchMaterias() {
      this.loading = true;
      try {
        const club_id = this.clubActual.id;
        const url = this.sinStock
          ? '/api/materia_prima_sin_stock'
          : '/api/materia_prima_stock';

        const res = await axios.get(url, { params: { club_id } });
        this.materias = res.data || [];
      } catch (error) {
        console.error('Error al listar materia prima:', error);
        this.materias = [];
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      return format(new Date(date), 'dd/MM/yyyy');
    },
    editMateria(materia_prima) {
      this.$emit('viewChange', { view: 'editar', materia_prima });
    },
    transformarProductoFinal(materia_prima) {
      this.$emit('viewChange', { view: 'producto_final_bulk', materia_prima });
    },
    ordenar(campo) {
      if (this.orden.campo === campo) {
        this.orden.asc = !this.orden.asc;
      } else {
        this.orden.campo = campo;
        this.orden.asc = true;
      }
    },
    copiar(texto) {
      navigator.clipboard.writeText(texto).then(() => {
        this.copiado = true;
        setTimeout(() => this.copiado = false, 1500);
      });
    },
    procesarMateriaPrima(materia_prima) {
      this.$emit('viewChange', { view: 'procesar', materia_prima });
    },
    editar(id) {
      this.$emit('viewChange', { view: 'editar', id });
    },
    limpiarFiltros() {
      this.filtros = {
        origen: '',
        codigo: '',
        codigo_origen: ''
      };
    },
    toggleDetalles(id) {
      if (this.detallesVisibles.includes(id)) {
        this.detallesVisibles = this.detallesVisibles.filter(x => x !== id);
      } else {
        this.detallesVisibles.push(id);
      }
    },
  },
  mounted() {
    this.fetchMaterias();
  }
};
</script>


<style scoped>
.listar-materia-prima {
  background: #fff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.listar-materia-prima h3 {
  font-weight: 600;
  color: #a84e1a;
}

.table thead th {
  cursor: pointer;
  background-color: #f6e9e4;
  /* Terracota claro */
  color: #7b3410;
  /* Terracota oscuro */
  font-size: 0.95rem;
}

.table tbody tr:hover {
  background-color: #f9f3f1;
}

.badge {
  background-color: #a84e1a;
  color: white;
  font-size: 0.8rem;
}

.btn-light {
  border-color: #a84e1a;
  color: #a84e1a;
}

.btn-light:hover {
  background-color: #a84e1a;
  color: white;
}

.btn-secondary {
  background-color: #ddd;
  border-color: #ccc;
}

.btn-secondary:hover {
  background-color: #bbb;
}

.copiado-toast {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background-color: #a84e1a;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: fade 1.5s forwards;
}

@keyframes fade {
  0% {
    opacity: 1;
  }

  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.filtros-bloque {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 20px;
}

.filtros-bloque {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #eee;
}

.filtros-bloque .btn-outline-secondary {
  font-size: 0.85rem;
}

.badge-stock {
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.badge-con-stock {
  background-color: #e6f4ea;
  color: #2e7d32;
  border-color: #c5e1c5;
}

.badge-sin-stock {
  background-color: #fff3f2;
  color: #c62828;
  border-color: #f8d7da;
}

.text-materia {
  color: #a84e1a;
  /* Terracota principal */
}

.bg-materia-light {
  background-color: #fef5f2;
  /* Color claro relacionado a materia prima */
}

.btn-outline-materia {
  border-color: #a84e1a;
  color: #a84e1a;
}

.btn-outline-materia:hover {
  background-color: #a84e1a;
  color: white;
}

.bg-danger-subtle {
  background-color: #fdecea;
}

.bg-success-subtle {
  background-color: #e6f4ea;
}

.pagination .page-link {
  color: #a84e1a;
}

.pagination .page-item.active .page-link {
  background-color: #a84e1a;
  color: white;
}
</style>
